import { type ChangeEvent, type FC, useRef } from "react";
import { useField } from "react-final-form";
import { styled } from "@mui/material/styles";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import DeleteIcon from "@mui/icons-material/Delete";
import { useTranslation } from "react-i18next";
import {
  useBannerLogic,
  BannerPlaceholder,
  BannerPreview,
} from "@/shared/components/Form/BannerField";

const Root = styled("div")(({ theme }) => ({
  position: "relative",
  overflow: "hidden",
  borderRadius: 4,
  width: "100%",
  paddingTop: "56.25%", // фиксированный контейнер 16:9 — у продукта только одна обложка (нет verticalPreview в схеме)
  height: 0,
  ...theme.applyStyles("dark", {
    border: "1px solid rgba(255, 255, 255, 0.23)",
  }),
  ...theme.applyStyles("light", {
    border: "1px solid rgba(0, 0, 0, 0.23)",
  }),
  "&:hover": {
    ...theme.applyStyles("dark", { borderColor: "rgba(255, 255, 255, 1)" }),
    ...theme.applyStyles("light", { borderColor: "rgba(0, 0, 0, 1)" }),
  },
}));

const ControlsBar = styled("div")({
  position: "absolute",
  right: 0,
  bottom: 0,
  left: 0,
  height: 51,
  backgroundColor: "rgba(0,0,0,0.9)",
  color: "white",
});

/**
 * Обложка продукта (референс: ProductForm/Edit.js — ImageField с одним
 * horizontalImage, без vertical/promo/background — у Product в нашей схеме
 * только previewId, см. ProductType). Тот же конвейер BannerField, что и в
 * форме видео (cabinet/features/videos/ui/PreviewImageField.tsx), но без
 * BannerToggle — формат всегда 16:9.
 */
export const ProductPreviewImageField: FC = () => {
  const { t } = useTranslation();
  const previewField = useField<string | null>("previewId");
  const inputRef = useRef<HTMLInputElement>(null);

  const value = { horizontalId: previewField.input.value ?? undefined };

  const handleChange = (next: { horizontalId?: string | null }) => {
    if (next.horizontalId !== value.horizontalId) {
      previewField.input.onChange(next.horizontalId ?? null);
    }
  };

  const {
    hover,
    currentImage,
    createPreviewResult,
    hasImage,
    isEmpty,
    handleFileChange,
    onDragEnter,
    onDragOver,
    onDragLeave,
    onDrop,
    onClickDelete,
  } = useBannerLogic({
    value,
    onChange: handleChange,
    orientation: "horizontal",
    successMessage: t("products.form.previewUploadedSnackbar"),
  });

  const onClickUpload = () => inputRef.current?.click();

  const onChangeFileInput = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) handleFileChange(file);
    event.target.value = "";
  };

  return (
    <Root
      style={hover ? { borderColor: "currentColor" } : undefined}
      onDragEnter={onDragEnter}
      onDragLeave={onDragLeave}
      onDragOver={onDragOver}
      onDrop={onDrop}
    >
      {isEmpty && (
        <BannerPlaceholder
          orientation="horizontal"
          required
          label={t("products.form.previewUploadLabel")}
          onUploadClick={onClickUpload}
          isLoading={createPreviewResult.loading}
        />
      )}

      {hasImage && currentImage && (
        <BannerPreview
          orientation="horizontal"
          imageUrl={currentImage}
          isLoading={createPreviewResult.loading}
        />
      )}

      {hasImage && (
        <ControlsBar>
          <Tooltip
            title={t("products.form.previewDeleteTooltip")}
            placement="top"
          >
            <IconButton
              onClick={onClickDelete}
              size="large"
              color="inherit"
              sx={{ position: "absolute", right: 1, bottom: 1 }}
            >
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        </ControlsBar>
      )}

      <Box
        component="input"
        accept="image/*"
        type="file"
        ref={inputRef}
        onChange={onChangeFileInput}
        sx={{
          position: "absolute",
          opacity: 0,
          inset: 0,
          pointerEvents: "none",
        }}
      />
    </Root>
  );
};
